//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import { getResultTypeConfig } from "../../../config/result-types";
import type { ResultFlag } from "@skyware-group/iris-api-model";

import type { Visit, FlaggingResult } from "../../../services/api/domains/lis/lis.types";
import { ResultFlagLevel, FlagSource } from "../../../services/api/domains/pending/pending.types";
import type { FlagItem, PendingResult } from "../../../services/api/domains/pending/pending.types";
import type { ExaminationIndexEntry, FlagData, MappedResult } from "./validationResults.types";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Validation Results Composable
 *
 * Transforms raw results into enriched display format with flagging and metadata.
 *
 * Responsibilities:
 * - Map pending results with flag data (level, colors, icons)
 * - Map current (published) results from visit examinations
 * - Enrich results with examination metadata (units, range)
 * - Build examination index for O(1) lookup
 * - Handle panel relationships (parent/child)
 * - Filter out duplicates (pending results take precedence)
 * - Determine if results can be published to LIS
 *
 * Data Enrichment:
 * - Pending Results: Raw result + flagging + examination metadata
 * - Current Results: Visit examination + flagging (no pending duplicate)
 * - Flag Data: Level, color, gradient, icon, text, flags array
 * - Examination Data: Units, range, optional, added, rank, panel
 *
 * @param key - Unique key for state isolation (default: "default")
 */
export const useValidationResults = (key: string = "default") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const {
    getFlagLevel,
    getFlagColor,
    getFlagGradient,
    getFlagText,
    getFlagIcon,
    getFlagDialogIcon,
    getFlagDialogColor,
  } = useFlagging();

  //
  // ============================================================================
  // CACHE
  // ============================================================================
  //

  /**
   * Cached examination index (memoized per visit)
   *
   * Purpose:
   * - Avoid rebuilding index on every mapPendingResults call
   * - O(1) examination metadata lookup
   * - Cleared when visit ID changes
   */
  let cachedExamIndex: Map<string, ExaminationIndexEntry> | null = null;

  /**
   * Cached visit ID (for index invalidation)
   *
   * Purpose:
   * - Track which visit the cached index belongs to
   * - Rebuild index when visit changes
   * - Null when no visit cached
   */
  let cachedVisitId: string | null = null;

  //
  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================
  //

  /**
   * Check if a result value is purely numeric
   *
   * Purpose:
   * Determine if a result contains only numeric characters (digits and decimal point).
   * Used to decide whether to display numericPrefix.
   *
   * Responsibilities:
   * - Trim whitespace from value
   * - Check if value matches numeric pattern
   * - Allow optional decimal point and digits
   * - Reject values with text, special characters, or mixed content
   *
   * Examples:
   * - "1.97" → true (purely numeric)
   * - "15.30" → true (purely numeric)
   * - ">5" → false (contains prefix in value)
   * - "NEG" → false (text result)
   * - "1.2-3.4" → false (range, not single number)
   * - "" → false (empty)
   *
   * @param value - Result value to check
   * @returns True if value is purely numeric, false otherwise
   */
  const isPurelyNumeric = (value: string | undefined | null): boolean => {
    if (!value) return false;
    const trimmed = value.trim();
    if (!trimmed) return false;

    // Check if the entire value is a number (including decimals, negative)
    // Regex: optional minus, one or more digits, optional decimal point and more digits
    return /^-?\d+(\.\d+)?$/.test(trimmed);
  };

  /**
   * Format numeric result with precision
   *
   * Purpose:
   * Apply correct decimal precision to numeric results for consistent display.
   * Only formats purely numeric values, leaves text results unchanged.
   *
   * Responsibilities:
   * - Check if value is purely numeric
   * - Parse numeric value
   * - Format with specified decimal places
   * - Return original value if not numeric or no precision specified
   *
   * Examples:
   * - formatWithPrecision("1.97", 3) → "1.970"
   * - formatWithPrecision("15.3", 2) → "15.30"
   * - formatWithPrecision("NEG", 2) → "NEG"
   * - formatWithPrecision("1.97", undefined) → "1.97"
   *
   * @param value - Result value to format
   * @param precision - Number of decimal places
   * @returns Formatted value or original if not applicable
   */
  const formatWithPrecision = (value: string | undefined | null, precision?: number): string => {
    // Return original if no value or no precision specified
    if (!value || precision === undefined || precision === null) {
      return value || "";
    }

    // Only format purely numeric values
    if (!isPurelyNumeric(value)) {
      return value;
    }

    // Parse and format with precision
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      return value;
    }

    return numericValue.toFixed(precision);
  };

  /**
   * Build examination lookup index from visit
   *
   * Purpose:
   * - Create Map for O(1) examination metadata lookup
   * - Index both examinations and panel items
   * - Store parent panel reference for items
   *
   * Responsibilities:
   * - Iterate through visit examinations
   * - Add each examination to Map (keyed by ID)
   * - For panels: Add each item with parent reference
   * - Extract metadata: units, range, optional, added, rank
   *
   * @param visit - Visit containing examinations
   * @returns Map<examId, metadata>
   */
  const buildExaminationIndex = (visit: Visit | null): Map<string, ExaminationIndexEntry> => {
    const map = new Map<string, ExaminationIndexEntry>();

    if (!visit?.examinations) return map;

    visit.examinations.forEach((exam) => {
      // Add parent examination
      map.set(exam.id, {
        id: exam.id,
        units: exam.units,
        rangeText: exam.rangeText,
        numericPrefix: (exam as any).numericPrefix,
        precision: exam.precision,
        isOptional: exam.isOptional,
        isAdded: exam.isAdded,
        rank: exam.rank,
        parentPanel: null,
      });

      // Add panel items with parent reference
      if (exam.$type === "P" && exam.items?.length) {
        exam.items.forEach((item) => {
          map.set(item.id, {
            id: item.id,
            units: item.units,
            rangeText: item.rangeText,
            numericPrefix: (item as any).numericPrefix,
            precision: item.precision,
            isOptional: item.isOptional,
            isAdded: item.isAdded,
            rank: exam.rank,
            parentPanel: {
              id: exam.id,
              name: exam.name,
            },
          });
        });
      }
    });

    return map;
  };

  /**
   * Get cached examination index or build new one
   *
   * Purpose:
   * - Memoize examination index per visit
   * - Avoid redundant index building
   * - Return cached index if visit ID matches
   *
   * Responsibilities:
   * - Check if cached index exists and matches visit ID
   * - If match: Return cached index
   * - If no match: Build new index, cache it, return it
   * - Update cachedVisitId for future calls
   *
   * @param visit - Visit to get index for
   * @returns Cached or newly built examination index
   */
  const getExaminationIndex = (visit: Visit | null): Map<string, ExaminationIndexEntry> => {
    // Return cached index if same visit
    if (visit?.id && cachedVisitId === visit.id && cachedExamIndex) {
      return cachedExamIndex;
    }

    // Build and cache new index
    cachedExamIndex = buildExaminationIndex(visit);
    cachedVisitId = visit?.id || null;

    return cachedExamIndex;
  };

  /**
   * Pre-compute all flag-related data
   *
   * Purpose:
   * - Avoid multiple calls to flag helper functions
   * - Compute all flag properties in one pass
   * - Return structured FlagData object
   *
   * Responsibilities:
   * - Calculate highest flag level from flags array
   * - Get color, gradient, icon, text for flag level
   * - Get dialog-specific icon and color for highest flag
   * - Return all flag data in one object
   *
   * @param flagLevel - Backend flag level (0-7)
   * @param flags - Array of flag items
   * @returns Structured flag data object
   */
  const computeFlagData = (flagLevel: number, flags: FlagItem[]): FlagData => {
    const highestFlagLevel = flags.length > 0 ? Math.max(...flags.map((f) => f.level)) : 0;

    return {
      flagLevel,
      flagColor: getFlagColor(flagLevel),
      flagGradient: getFlagGradient(flagLevel),
      flagText: getFlagText(flagLevel),
      flagIcon: getFlagIcon(flagLevel),
      flags,
      flagCount: flags.length,
      highestFlagLevel,
      highestFlagIcon: highestFlagLevel > 0 ? getFlagDialogIcon(highestFlagLevel) : "mdi-help-circle-outline",
      highestFlagColor: highestFlagLevel > 0 ? getFlagDialogColor(highestFlagLevel) : "grey-darken-2",
    };
  };

  const mapResultFlagSeverity = (severity: number): ResultFlagLevel => {
    switch (severity) {
      case 0:
        return ResultFlagLevel.Information;
      case 1:
        return ResultFlagLevel.Warning;
      case 2:
        return ResultFlagLevel.Danger;
      case 3:
        return ResultFlagLevel.Panic;
      default:
        return ResultFlagLevel.Unknown;
    }
  };

  const mapResultFlags = (flags: ResultFlag[] | null | undefined): FlagItem[] => {
    if (!flags?.length) return [];
    return flags.map((flag) => ({
      level: mapResultFlagSeverity(flag.severity),
      source: flag.isInstrumentFlag ? FlagSource.Instrument : FlagSource.Processor,
      code: flag.code,
      description: flag.description ?? "",
    }));
  };

  const getPendingResultFlags = (result: PendingResult): FlagItem[] => {
    return mapResultFlags(result.flags);
  };

  /**
   * Enrich pending result with examination and flag data
   *
   * Purpose:
   * - Combine raw result with metadata for display
   * - Add flag data (colors, icons, level)
   * - Add examination data (units, range, panel)
   * - Add current value if exists in visit
   *
   * Responsibilities:
   * - Look up examination metadata by mappingKey
   * - Extract units, range, optional, added, rank
   * - Determine panel relationship (parent/child)
   * - Compute flag data from flag level
   * - Check for existing current value in visit
   * - Merge all data into single result object
   *
   * @param result - Raw pending result
   * @param examIndex - Examination lookup index
   * @param flagLevel - Computed flag level
   * @param visit - Current visit (for currentValue)
   * @returns Enriched result with all metadata
   */
  const enrichPendingResult = (
    result: PendingResult,
    examIndex: Map<string, ExaminationIndexEntry>,
    flagLevel: number,
    visit: Visit | null,
  ): MappedResult & FlagData => {
    // The result-cell gradient reflects ONLY the pending value's reference-range
    // level (from /lis/flagging). Instrument/processor flags are a separate
    // signal surfaced via the badge (flags / flagCount / highestFlag*) and must
    // NOT tint the cell — a value inside its reference range stays untinted even
    // when an instrument flag is present.
    const flags = getPendingResultFlags(result);
    const flagData = computeFlagData(flagLevel, flags);
    const matchedExam = examIndex.get(result.mappingKey ?? "");

    // Default values
    let units = "";
    let rangeText = "";
    let panelName: string | null = null;
    let isPanelItem = false;
    let parentPanelId: string | null = null;
    let isOptional = false;
    let isAdded = true;
    let rank: number | undefined = undefined;
    let currentValue: string | null = (result as any).currentValue || null;
    let currentValueFlagLevel = 0;

    // Enrich with examination data if available
    if (matchedExam) {
      units = matchedExam.units || "";
      rangeText = matchedExam.rangeText || "";
      isOptional = matchedExam.isOptional || false;
      isAdded = matchedExam.isAdded ?? true;
      rank = matchedExam.rank;

      // Handle panel relationship
      if (matchedExam.parentPanel) {
        panelName = matchedExam.parentPanel.name;
        isPanelItem = true;
        parentPanelId = matchedExam.parentPanel.id;
      }
    }

    // Look up the matching visit examination for the current (published) value
    // and its OWN reference-range flag level. The current value's gradient is
    // independent of the pending value's gradient — each cell reflects where its
    // own value falls relative to the reference range (e.g. a pending value in
    // range stays untinted while an out-of-range current value shows red).
    if (visit?.examinations) {
      const currentExam = visit.examinations
        .flatMap((e) => (e.items?.length ? e.items : [e]))
        .find((e) => e.id === result.mappingKey);

      if (currentExam) {
        if (!currentValue) {
          currentValue = currentExam.result || currentExam.numericResult?.toString() || null;
        }
        currentValueFlagLevel = currentExam.flagLevel || 0;
      }
    }

    // Enrich with result type configuration if not already set
    const typeConfig = getResultTypeConfig(result.testCode ?? "");
    const resultType = result.resultType || typeConfig?.resultType || "numeric";
    const allowedValues = result.allowedValues || typeConfig?.allowedValues;

    // Extract numericPrefix from pending result or matched examination
    // Priority: If numericResult is not null, show prefix
    // Fallback: Only show if displayResult is purely numeric
    const numericPrefix =
      result.numericResult != null
        ? (result as any).numericPrefix || matchedExam?.numericPrefix
        : isPurelyNumeric(result.displayResult)
          ? (result as any).numericPrefix || matchedExam?.numericPrefix
          : undefined;

    // Get precision from matched examination
    const precision = matchedExam?.precision;

    // Format displayResult with precision if numeric
    const formattedDisplayResult = formatWithPrecision(result.displayResult, precision);

    return {
      id: result.id,
      lisName: result.lisName ?? undefined,
      displayResult: formattedDisplayResult,
      numericPrefix,
      numericResult: result.numericResult,
      textResult: result.textResult ?? undefined,
      mappingKey: result.mappingKey ?? undefined,
      resultTime: result.resultTime,
      units,
      rangeText,
      panelName,
      isPanelItem,
      parentPanelId,
      isOptional,
      isAdded,
      rank,
      currentValue,
      currentValueFlagLevel,
      currentValueFlagGradient: getFlagGradient(currentValueFlagLevel),
      isAccepted: (result as any).isAccepted || false,
      isEdited: result.isEdited || false,
      editHistory: result.editHistory || [],
      resultType,
      allowedValues,
      ...flagData,
    };
  };

  /**
   * Enrich examination item with flag data
   *
   * Purpose:
   * - Convert visit examination to display format
   * - Add flag data for UI styling
   * - Handle panel relationships
   *
   * Responsibilities:
   * - Extract flag level from examination
   * - Compute flag data (colors, icons)
   * - Extract result value (result or numericResult)
   * - Set panel flags (isPanel, isPanelItem)
   * - Include parent panel info if applicable
   *
   * @param exam - Examination or item
   * @param isPanel - Whether this is a panel container
   * @param isPanelItem - Whether this is a panel child item
   * @param parentPanelId - Parent panel ID (if applicable)
   * @param panelName - Parent panel name (if applicable)
   * @returns Enriched result with flag data
   */
  const enrichCurrentResult = (
    exam: any,
    isPanel: boolean,
    isPanelItem: boolean,
    parentPanelId: string | null,
    panelName: string | null,
  ): MappedResult & FlagData => {
    const flagLevel = exam.flagLevel || 0;
    const flags: FlagItem[] = [];
    const flagData = computeFlagData(flagLevel, flags);
    const resultValue = exam.result || exam.numericResult?.toString() || "";

    // Only include numericPrefix if result is purely numeric
    const numericPrefix = isPurelyNumeric(resultValue) ? exam.numericPrefix : undefined;

    // Format result with precision if numeric
    const formattedCurrentValue = formatWithPrecision(resultValue, exam.precision);

    return {
      id: exam.id,
      name: exam.name,
      isPanel,
      isPanelItem,
      parentPanelId,
      panelName,
      result: "",
      currentValue: formattedCurrentValue,
      // For a standalone current result the displayed value IS the current value,
      // so its current-value cell gradient mirrors this row's own flag level.
      currentValueFlagLevel: flagLevel,
      currentValueFlagGradient: flagData.flagGradient,
      numericPrefix,
      numericResult: exam.numericResult ?? null,
      units: exam.units || "",
      rangeText: exam.rangeText || "",
      resultTime: exam.resultTime || "",
      status: exam.status,
      rank: exam.rank,
      ...flagData,
    };
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  /**
   * Map pending results with flagging and visit data
   *
   * Purpose:
   * - Transform raw pending results into enriched display format
   * - Combine results, flags, and examination metadata
   * - Prepare data for UI rendering
   *
   * Responsibilities:
   * - Get or build examination index
   * - For each result: Look up flag level
   * - Enrich result with exam metadata and flag data
   * - Return array of enriched results
   *
   * @param results - Raw pending results from backend
   * @param visit - Current visit with examinations
   * @param flagging - Flagging calculation results
   * @returns Array of enriched results with metadata
   */
  const mapPendingResults = (
    results: PendingResult[],
    visit: Visit | null,
    flagging: FlaggingResult[],
  ): (MappedResult & FlagData)[] => {
    if (!results?.length) return [];

    const examIndex = getExaminationIndex(visit);

    return results.map((result) => {
      const flagLevel = getFlagLevel(result.id, flagging as any);
      return enrichPendingResult(result, examIndex, flagLevel, visit);
    });
  };

  /**
   * Map current results from visit examinations
   *
   * Purpose:
   * - Transform visit examinations into enriched display format
   * - Filter out duplicates (exams with pending results)
   * - Handle panel hierarchies (parent/child relationships)
   *
   * Responsibilities:
   * - Build set of pending mappingKeys for O(1) lookup
   * - For each examination:
   *   - Skip if has pending results (avoid duplicates)
   *   - Handle panels: Add items as children
   *   - Handle regular exams: Add as standalone
   * - Enrich with examination metadata + flag data
   *
   * @param visit - Current visit with examinations
   * @param pendingResults - Pending results to check against (for duplicate filtering)
   * @returns Array of enriched current results without duplicates
   */
  const mapCurrentResults = (
    visit: Visit | null,
    pendingResults: PendingResult[] = [],
  ): (MappedResult & FlagData)[] => {
    if (!visit?.examinations?.length) return [];

    // Build set of mapping keys that have pending results
    const pendingMappingKeys = new Set(
      pendingResults.map((r) => r.mappingKey).filter(Boolean), // Remove null/undefined
    );

    const results: (MappedResult & FlagData)[] = [];

    visit.examinations.forEach((exam) => {
      // Skip examination if it has pending results
      if (pendingMappingKeys.has(exam.id)) {
        return; // Don't show in current results
      }

      const isPanel = exam.$type === "P";
      const isRegular = exam.$type === "T";
      const hasItems = exam.items?.length;

      // Handle panel examinations
      if (isPanel && hasItems) {
        // Add panel items as children
        exam.items!.forEach((item) => {
          // Skip panel item if it has pending results
          if (pendingMappingKeys.has(item.id)) {
            return;
          }

          results.push(enrichCurrentResult(item, false, true, exam.id, exam.name));
        });
      }
      // Handle regular examinations
      else if (isRegular) {
        results.push(enrichCurrentResult(exam, false, false, null, null));
      }
      // Handle panel header (if needed)
      else if (isPanel) {
        results.push(enrichCurrentResult(exam, true, false, null, null));
      }
    });

    return results;
  };

  /**
   * Check if a result can be published to LIS
   *
   * Purpose:
   * - Validate that result meets publishing criteria
   * - Prevent invalid results from being sent to LIS
   * - Enforce optional examination rules
   *
   * Responsibilities:
   * - Check visit exists with examinations
   * - Look up examination by mappingKey
   * - Verify optional tests are explicitly added
   * - Return boolean: can publish or not
   *
   * @param result - Result to check (with mappingKey, isOptional, isAdded)
   * @param visit - Current visit with examinations
   * @returns True if result can be published to LIS, false otherwise
   */
  const canPublishResult = (
    result: {
      mappingKey: string;
      isOptional?: boolean;
      isAdded?: boolean;
    },
    visit: Visit | null,
  ): boolean => {
    if (!visit?.examinations?.length) return false;

    const examIndex = getExaminationIndex(visit);
    const exam = examIndex.get(result.mappingKey);

    if (!exam) return false;

    // Optional tests must be explicitly added
    if (result.isOptional && !result.isAdded) {
      return false;
    }

    return true;
  };

  //
  // ============================================================================
  // EXPORTS
  // ============================================================================
  //

  return {
    mapPendingResults,
    mapCurrentResults,
    canPublishResult,
  };
};
